import { useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import Chart, {
    ArgumentAxis,
    Font,
    Label,
    Legend,
    Series,
    Title,
    Tooltip,
    ValueAxis,
} from 'devextreme-react/chart';
import PieChart, {
    Connector,
    Font as PieFont,
    Label as PieLabel,
    Legend as PieLegend,
    Series as PieSeries,
    Title as PieTitle,
    Tooltip as PieTooltip,
} from 'devextreme-react/pie-chart';
import PolarChart, {
    Font as PolarFont,
    Legend as PolarLegend,
    Series as PolarSeries,
    Title as PolarTitle,
    Tooltip as PolarTooltip,
} from 'devextreme-react/polar-chart';
import { DashboardCard } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import {
    DASHBOARD_CHART_VIEW_CONFIG,
    DASHBOARD_DISTRIBUTION_COLORS,
    DashboardChartView,
    getDashboardDistributionColor,
} from '../types';
import { useGradeLevelData } from '../contexts';
import { GradeLevelFilterControls } from './GradeLevelFilterControls';

export const GradeLevelChartCard = () => {
    const { data, loading, error, filter } = useGradeLevelData();
    const chartRef = useRef<Chart>(null);
    const pieChartRef = useRef<PieChart>(null);
    const polarChartRef = useRef<PolarChart>(null);
    const [chartView, setChartView] = useState<DashboardChartView>(
        DASHBOARD_CHART_VIEW_CONFIG.gradeLevel.recommendedView
    );
    const rows = useMemo(() => {
        const gradeLevels = new Map<string, {
            gradeLevel: string;
            displayOrder: number;
            studentCount: number;
        }>();

        data.forEach(item => {
            const row = gradeLevels.get(item.gradeLevelName);
            if (row) {
                row.studentCount += item.studentCount;
            } else {
                gradeLevels.set(item.gradeLevelName, {
                    gradeLevel: item.gradeLevelName,
                    displayOrder: item.gradeLevelDisplayOrder,
                    studentCount: item.studentCount,
                });
            }
        });

        return Array.from(gradeLevels.values())
            .sort((left, right) => left.displayOrder - right.displayOrder);
    }, [data]);
    const comparisonYears = useMemo(
        () => Array.from(new Set(data.map(item => item.year))).sort((a, b) => b - a),
        [data]
    );
    const comparisonRows = useMemo(() => {
        const gradeLevels = new Map<string, Record<string, string | number>>();
        data.filter(item => comparisonYears.includes(item.year)).forEach(item => {
            const row = gradeLevels.get(item.gradeLevelName) || {
                gradeLevel: item.gradeLevelName,
                displayOrder: item.gradeLevelDisplayOrder,
            };
            row[`year${item.year}`] = item.studentCount;
            gradeLevels.set(item.gradeLevelName, row);
        });
        return Array.from(gradeLevels.values())
            .sort((left, right) => Number(left.displayOrder) - Number(right.displayOrder));
    }, [data, comparisonYears]);
    const yearsLabel = filter.schoolYears.length
        ? filter.schoolYears.map(year => `${year}-${year + 1}`).join(', ')
        : 'Current School Year';
    const exportName = `Grade_Level_${filter.schoolYears.join('_') || 'CurrentYear'}`;
    const handleExport = () => {
        if (chartView === 'pie' || chartView === 'fullPie') {
            pieChartRef.current?.instance.exportTo(exportName, 'png');
        } else if (chartView === 'polar') {
            polarChartRef.current?.instance.exportTo(exportName, 'png');
        } else {
            chartRef.current?.instance.exportTo(exportName, 'png');
        }
    };
    const seriesType = chartView === 'area' ? 'area'
        : chartView === 'spline' ? 'spline'
        : chartView === 'splineArea' ? 'splinearea'
        : chartView === 'stackedBar' ? 'stackedbar'
        : chartView === 'line' ? 'line'
        : 'bar';

    return <DashboardCard
        title="Grade Level"
        onExport={handleExport}
        rightActions={<DashboardChartViewSelect
            dashboard="gradeLevel"
            value={chartView}
            onChange={setChartView}
            iconOnly
        />}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <GradeLevelFilterControls />
            {loading ? <Skeleton variant="rounded" height={400} /> :
                error ? <Typography color="error" align="center">{error}</Typography> :
                rows.length === 0 ? <Typography align="center">No grade-level data available</Typography> :
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {chartView === 'pie' || chartView === 'fullPie' ? <PieChart
                        ref={pieChartRef}
                        dataSource={rows}
                        height="100%"
                        type={chartView === 'fullPie' ? 'pie' : 'doughnut'}
                        palette={DASHBOARD_DISTRIBUTION_COLORS}
                    >
                        <PieTitle text="Grade Level Distribution" subtitle={yearsLabel}>
                            <PieFont size={18} weight={600} />
                        </PieTitle>
                        <PieSeries argumentField="gradeLevel" valueField="studentCount">
                            <PieLabel visible position="outside"><Connector visible /></PieLabel>
                        </PieSeries>
                        <PieLegend visible={false} />
                        <PieTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({
                            text: `${argumentText}: ${valueText} students`,
                        })} />
                    </PieChart> : chartView === 'polar' ? <PolarChart
                        ref={polarChartRef}
                        dataSource={rows}
                        height="100%"
                        palette={DASHBOARD_DISTRIBUTION_COLORS}
                    >
                        <PolarTitle text="Grade Level Distribution" subtitle={yearsLabel}>
                            <PolarFont size={18} weight={600} />
                        </PolarTitle>
                        <PolarSeries type="bar" argumentField="gradeLevel" valueField="studentCount" />
                        <PolarLegend visible={false} />
                        <PolarTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({
                            text: `${argumentText}: ${valueText} students`,
                        })} />
                    </PolarChart> : <Chart
                        ref={chartRef}
                        dataSource={chartView === 'comparisonBar' ? comparisonRows : rows}
                        height="100%"
                        rotated={chartView === 'horizontalBar'}
                        customizePoint={chartView === 'comparisonBar' ? undefined : ({ data }) => ({
                            color: getDashboardDistributionColor(data.displayOrder - 1),
                        })}
                    >
                        <Title text="Grade Level Distribution" subtitle={yearsLabel}>
                            <Font size={18} weight={600} />
                        </Title>
                        <ArgumentAxis
                            argumentType="string"
                            label={{ rotationAngle: -45, font: { size: 13, weight: 600 } }}
                        />
                        <ValueAxis allowDecimals={false} visible={false} />
                        {chartView === 'comparisonBar' ? comparisonYears.map((year, index) =>
                            <Series
                                key={year}
                                type="bar"
                                argumentField="gradeLevel"
                                valueField={`year${year}`}
                                name={`${year}-${year + 1}`}
                                color={getDashboardDistributionColor(index)}
                            />
                        ) : <Series type={seriesType} argumentField="gradeLevel" valueField="studentCount" name="Students">
                            <Label visible={false} />
                        </Series>}
                        <Legend visible={chartView === 'comparisonBar'} />
                        <Tooltip
                            enabled
                            customizeTooltip={({ argumentText, valueText }) => ({
                                text: `${argumentText}: ${valueText} students`,
                            })}
                        />
                    </Chart>}
                </Box>}
        </Box>
    </DashboardCard>;
};
